import { useEffect, useRef, useState } from "react";
import { getInstalledRoomInviteBridge } from "./DDZInviteBridge";
import { isRoomInviteRegisteredPlayer, roomInviteGamePath, roomInviteHomePath,
  roomInviteRegistrationPath, ROOM_INVITE_REGISTRY } from "./RoomInvite";

// Registry-driven routing matches the Egypt hall. The provider owns the single public native receiver.
export function useRoomInvite({ location, history, getPersist, setPersist, getGameUrl, hallOrigin,
  registry = ROOM_INVITE_REGISTRY }) {
  const handled = useRef(""), failedRoute = useRef(null);
  const bridge = typeof window !== "undefined" ? getInstalledRoomInviteBridge(window) : null;
  const [pending, setPending] = useState(() => bridge && bridge.pending());
  const loaded = getPersist("dataLoaded"), userInfo = getPersist("user_info");
  useEffect(() => {
    if (!bridge) return;
    const unsubscribe = bridge.subscribe(setPending);
    setPending(bridge.pending());
    return unsubscribe;
  }, [bridge]);

  useEffect(() => {
    if (!loaded) { failedRoute.current = null; return; }
    // DDZ invitations remain server-token based even when mixed with room fields.
    if (new URLSearchParams(location.search).has("invite_token")) return;
    if (!["/home", "/"].includes(location.pathname)) { handled.current = ""; failedRoute.current = null; return; }
    const returnPath = roomInviteHomePath(location.search, registry, true);
    if (!returnPath) { handled.current = ""; failedRoute.current = null; return; }
    if (bridge && bridge.isSupersededPath(roomInviteHomePath(location.search, registry))) return;
    const current = bridge && bridge.pending();
    const matchesNative = current && current.kind === "room" &&
      current.path === roomInviteHomePath(location.search, registry);
    // The provider waits for identity, game and wallet readiness before handing off a native delivery.
    if (matchesNative && current.phase !== "routing") return;
    const registered = isRoomInviteRegisteredPlayer(userInfo);
    const key = `${registered ? "game" : "register"}:${returnPath}:${matchesNative ? current.deliveryId : ""}`;
    const retryContext = [key, location.pathname, location.search, location.key, userInfo && userInfo.token,
      userInfo && userInfo.rid, userInfo && userInfo.is_guest, userInfo && userInfo.is_demo, getGameUrl, hallOrigin, registry];
    if (failedRoute.current && retryContext.every((value, index) => value === failedRoute.current[index])) return;
    if (handled.current === key) return;
    const query = new URLSearchParams(returnPath.split("?")[1]);
    try {
      const destination = registered ? roomInviteGamePath(query, getGameUrl, hallOrigin, registry) :
        roomInviteRegistrationPath(returnPath);
      if (!destination) return;
      handled.current = key;
      setPersist("agent_rid", query.get("aid") || "");
      setPersist("agent_from", query.get("f") || "");
      if (!registered) {
        setPersist("isLogin", false);
        setPersist("login_back_url", returnPath);
      }
      history.replace(destination);
      failedRoute.current = null;
    } catch (_) {
      handled.current = "";
      failedRoute.current = retryContext;
      // Keep the provider's routing gate: retry on navigation/readiness changes, without reopening its effect loop.
    }
  }, [loaded, location.pathname, location.search, location.key, userInfo, getGameUrl, hallOrigin, history,
    setPersist, registry, bridge, pending]);
}
